import React, { useCallback, useEffect, useRef, useState } from "react";
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ENV } from "../../utils/configs";
import { authStreamFetch } from "../../utils/authStreamFetch";
import { useVoiceInput } from "../../hooks/useVoiceInput";
import MarkdownRenderer from "../MarkdownRenderer/MarkdownRenderer";

/** Match Topbar.styled.js — same navy as sidebar/header */
const APP_HEADER_NAVY = "rgb(44, 52, 123)";
/** Ask FlashDice InputArea send / accents */
const BRAND_ACCENT = "#3F49A7";
const BRAND_ACCENT_HOVER = "#2c357a";
const ASK_SURFACE = "#f8fafc";
const INPUT_BORDER = "#e2e8f0";

const PANEL_WIDTH = 450;
const PANEL_MAX_HEIGHT = 500;
const MESSAGE_AREA_MAX_HEIGHT = 400;

/** Flush to viewport corner; safe-area keeps clear of home indicator where supported */
const LAUNCHER_RIGHT = "env(safe-area-inset-right, 0px)";
const LAUNCHER_BOTTOM = "env(safe-area-inset-bottom, 0px)";

const WORKFLOW_SESSION_KEY = "flashdice_workflow_chat_session_id";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text: "How can I help you today? I can guide you through FlashDice workflows and app features.",
};

function getOrCreateWorkflowSessionId() {
  try {
    let id = sessionStorage.getItem(WORKFLOW_SESSION_KEY);
    if (!id) {
      id = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(WORKFLOW_SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
}

/** Strip markdown-ish syntax so TTS reads natural speech */
function textForTts(markdown) {
  if (!markdown || typeof markdown !== "string") return "";
  let s = markdown;
  s = s.replace(/```[\s\S]*?```/g, " ");
  s = s.replace(/`([^`]+)`/g, "$1");
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  s = s.replace(/^#{1,6}\s+/gm, "");
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/\*([^*]+)\*/g, "$1");
  s = s.replace(/^[\s>*-]+/gm, "");
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

function cancelAssistantSpeech() {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/** OpenAI-compatible SSE stream: lines like `data: {...}` or `data: [DONE]` */
async function readChatCompletionStream(body, onDelta) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":")) continue;
        if (trimmed === "data: [DONE]") continue;
        if (!trimmed.startsWith("data:")) continue;
        const payload = trimmed.startsWith("data: ")
          ? trimmed.slice(6).trim()
          : trimmed.slice(5).trim();
        if (payload === "[DONE]") continue;
        try {
          const chunk = JSON.parse(payload);
          const choice = chunk?.choices?.[0];
          const piece =
            choice?.delta?.content ??
            choice?.message?.content ??
            "";
          if (piece) onDelta(piece);
        } catch {
          /* ignore malformed chunk */
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

const FloatingChatbot = () => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() =>
    getOrCreateWorkflowSessionId()
  );
  const [messages, setMessages] = useState(() => [{ ...WELCOME_MESSAGE }]);
  const [ttsPlayingId, setTtsPlayingId] = useState(null);
  const [voiceError, setVoiceError] = useState(null);
  const scrollRef = useRef(null);

  const handleVoiceTranscript = useCallback((transcript) => {
    setInput(transcript);
  }, []);

  const {
    isListening,
    isSupported: voiceSupported,
    error: voiceInputError,
    toggleListening,
    stopListening,
  } = useVoiceInput(handleVoiceTranscript);

  useEffect(() => {
    if (voiceInputError) {
      setVoiceError(voiceInputError);
      const timer = setTimeout(() => setVoiceError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [voiceInputError]);

  const handleVoiceMicClick = useCallback(() => {
    if (!voiceSupported) {
      setVoiceError("Voice input is not supported in your browser.");
      return;
    }
    if (!isListening) {
      setInput("");
      setVoiceError(null);
      cancelAssistantSpeech();
      setTtsPlayingId(null);
    }
    toggleListening();
  }, [voiceSupported, isListening, toggleListening]);

  const ttsSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const isDark = theme.palette.mode === "dark";
  const messagesBg = isDark ? theme.palette.grey[900] : ASK_SURFACE;
  const assistantBubbleBg = isDark ? theme.palette.grey[800] : "#ffffff";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) {
      cancelAssistantSpeech();
      setTtsPlayingId(null);
      stopListening();
    }
  }, [open]);

  useEffect(
    () => () => {
      cancelAssistantSpeech();
    },
    []
  );

  const speakOrToggleAssistant = useCallback(
    (messageId, rawText) => {
      if (!ttsSupported) return;
      if (ttsPlayingId === messageId) {
        cancelAssistantSpeech();
        setTtsPlayingId(null);
        return;
      }
      cancelAssistantSpeech();
      const plain = textForTts(rawText);
      if (!plain) return;
      const u = new SpeechSynthesisUtterance(plain);
      u.lang =
        typeof navigator !== "undefined" && navigator.language
          ? navigator.language
          : "en-US";
      u.onend = () => {
        setTtsPlayingId((cur) => (cur === messageId ? null : cur));
      };
      u.onerror = () => {
        setTtsPlayingId((cur) => (cur === messageId ? null : cur));
      };
      setTtsPlayingId(messageId);
      window.speechSynthesis.speak(u);
    },
    [ttsSupported, ttsPlayingId]
  );

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open, messages]);

  const handleNewSession = useCallback(() => {
    if (sending) return;
    cancelAssistantSpeech();
    setTtsPlayingId(null);
    stopListening();
    const newId = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    try {
      sessionStorage.setItem(WORKFLOW_SESSION_KEY, newId);
    } catch {
      /* storage unavailable */
    }
    setSessionId(newId);
    setMessages([{ ...WELCOME_MESSAGE }]);
    setInput("");
  }, [sending, stopListening]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const apiUrl = (ENV.workflow_chat_api_url || "").trim();

    const userId = `u_${Date.now()}`;
    setMessages((prev) => [...prev, { id: userId, role: "user", text }]);
    setInput("");
    setSending(true);

    const historyForApi = [...messages, { role: "user", text }].map((m) => ({
      role: m.role,
      content: m.text,
    }));

    const assistantId = `a_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    try {
      const res = await authStreamFetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "text/event-stream, application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: ENV.workflow_chat_model || "gpt-4o-mini",
          messages: historyForApi,
          temperature: 0.7,
          stream: true,
          session_id: sessionId,
        }),
      });

      if (!res.ok) {
        const raw = await res.text();
        let data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          /* plain text error */
        }
        const errMsg =
          data?.error?.message ||
          data?.detail ||
          (raw ? raw.slice(0, 240) : res.statusText);
        throw new Error(errMsg || `Request failed (${res.status})`);
      }

      if (!res.body) {
        throw new Error("Empty response body");
      }

      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", text: "" },
      ]);

      await readChatCompletionStream(res.body, (piece) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, text: m.text + piece } : m
          )
        );
      });

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId && !m.text.trim()
            ? {
                ...m,
                text: "No reply content was returned.",
              }
            : m
        )
      );
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Something went wrong. Try again.";
      const errText = `Could not reach the assistant: ${msg}`;
      setMessages((prev) => {
        if (prev.some((m) => m.id === assistantId)) {
          return prev.map((m) =>
            m.id === assistantId ? { ...m, text: errText } : m
          );
        }
        return [
          ...prev,
          { id: `a_${Date.now()}`, role: "assistant", text: errText },
        ];
      });
    } finally {
      setSending(false);
    }
  }, [input, sending, messages, sessionId]);

  return (
    <Box
      sx={{
        position: "fixed",
        right: 5,
        bottom: 5,
        paddingRight: LAUNCHER_RIGHT,
        paddingBottom: LAUNCHER_BOTTOM,
        zIndex: (t) => t.zIndex.drawer + 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 1,
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
    >
      {open && (
        <Paper
          elevation={2}
          sx={{
            width: PANEL_WIDTH,
            maxWidth: "calc(100vw - 48px)",
            maxHeight: PANEL_MAX_HEIGHT,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            mb: 0.5,
            border: `1px solid ${INPUT_BORDER}`,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              bgcolor: APP_HEADER_NAVY,
              color: "common.white",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              AI Assistant
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Tooltip title="New conversation">
                <span>
                  <IconButton
                    size="small"
                    aria-label="New conversation"
                    onClick={handleNewSession}
                    disabled={sending}
                    sx={{
                      color: "common.white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    <RestartAltOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                aria-label="Close chat"
                onClick={() => setOpen(false)}
                sx={{
                  color: "common.white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              minHeight: 160,
              maxHeight: MESSAGE_AREA_MAX_HEIGHT,
              overflowY: "auto",
              px: 1.5,
              py: 1.25,
              bgcolor: messagesBg,
            }}
          >
            {messages
              .filter(
                (m) =>
                  m.role !== "assistant" ||
                  (typeof m.text === "string" && m.text.trim() !== "")
              )
              .map((m, idx, visible) => {
                let prevUserQuestion = "";
                if (m.role === "assistant") {
                  for (let i = idx - 1; i >= 0; i--) {
                    if (visible[i].role === "user") {
                      prevUserQuestion = visible[i].text;
                      break;
                    }
                  }
                }

                return (
                  <Box
                    key={m.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        m.role === "user" ? "flex-end" : "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "90%",
                        px: 1.25,
                        py: m.role === "assistant" ? 1 : 0.875,
                        borderRadius: 1,
                        border:
                          m.role === "assistant"
                            ? `1px solid ${isDark ? theme.palette.grey[700] : INPUT_BORDER}`
                            : "none",
                        bgcolor:
                          m.role === "user"
                            ? BRAND_ACCENT
                            : assistantBubbleBg,
                        color:
                          m.role === "user"
                            ? "#ffffff"
                            : theme.palette.text.primary,
                      }}
                    >
                      {m.role === "user" ? (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontSize: "0.8125rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {m.text}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              "& .markdown-renderer": {
                                fontSize: "0.8125rem",
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                              },
                              "& .markdown-renderer p:first-of-type": {
                                marginTop: 0,
                              },
                              "& .markdown-renderer p:last-child": {
                                marginBottom: 0,
                              },
                            }}
                          >
                            <MarkdownRenderer
                              content={m.text}
                              question={prevUserQuestion}
                              nlp_message_id={null}
                              messageFeedback={null}
                              messageModelId={null}
                              isLastAssistantMessage={
                                !visible
                                  .slice(idx + 1)
                                  .some((x) => x.role === "assistant")
                              }
                            />
                          </Box>
                          <Tooltip
                            title={
                              !ttsSupported
                                ? "Speech not supported in this browser"
                                : ttsPlayingId === m.id
                                  ? "Stop"
                                  : "Listen"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label={
                                  ttsPlayingId === m.id
                                    ? "Stop reading"
                                    : "Listen to reply"
                                }
                                disabled={
                                  !ttsSupported || !textForTts(m.text)
                                }
                                onClick={() =>
                                  speakOrToggleAssistant(m.id, m.text)
                                }
                                sx={{
                                  flexShrink: 0,
                                  mt: -0.25,
                                  color:
                                    ttsPlayingId === m.id
                                      ? BRAND_ACCENT
                                      : theme.palette.text.secondary,
                                  "&:hover": {
                                    bgcolor: alpha(BRAND_ACCENT, 0.08),
                                  },
                                }}
                              >
                                {ttsPlayingId === m.id ? (
                                  <StopRoundedIcon sx={{ fontSize: 18 }} />
                                ) : (
                                  <VolumeUpOutlinedIcon sx={{ fontSize: 18 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
          </Box>

          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              bgcolor: "background.paper",
              borderTop: `1px solid ${INPUT_BORDER}`,
            }}
          >
            {voiceError && (
              <Box
                sx={{
                  mb: 0.75,
                  px: 1,
                  py: 0.5,
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 1,
                  fontSize: "0.6875rem",
                  color: "#dc2626",
                  lineHeight: 1.35,
                }}
              >
                {voiceError}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: `1px solid ${INPUT_BORDER}`,
                borderRadius: 999,
                backgroundColor: "#ffffff",
                px: 1,
                py: 0.25,
                minHeight: 0,
                transition: "all 0.2s ease",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                "&:focus-within": {
                  border: `1px solid ${BRAND_ACCENT}`,
                  boxShadow: "0 2px 8px rgba(63, 73, 167, 0.12)",
                },
                ...(isDark && {
                  backgroundColor: theme.palette.grey[900],
                  borderColor: theme.palette.grey[700],
                  "&:focus-within": {
                    borderColor: BRAND_ACCENT,
                  },
                }),
              }}
            >
              <TextField
                fullWidth
                placeholder={
                  sending
                    ? "Waiting for reply…"
                    : isListening
                      ? "Listening…"
                      : "Type or use the mic…"
                }
                value={input}
                disabled={sending}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !sending) {
                    e.preventDefault();
                    send();
                  }
                }}
                multiline
                maxRows={2}
                minRows={1}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: 0,
                  },
                  "& .MuiInputBase-input": {
                    py: 0.375,
                    fontSize: "0.75rem",
                    lineHeight: 1.35,
                    color: isDark ? theme.palette.common.white : "#1e293b",
                  },
                }}
              />
              <Tooltip
                title={
                  !voiceSupported
                    ? "Voice input not supported"
                    : isListening
                      ? "Stop recording"
                      : "Voice input"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label={
                      isListening ? "Stop voice input" : "Start voice input"
                    }
                    onClick={handleVoiceMicClick}
                    disabled={sending || !voiceSupported}
                    sx={{
                      flexShrink: 0,
                      alignSelf: "center",
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      padding: 0,
                      color: isListening ? "#dc2626" : "#64748b",
                      backgroundColor: isListening ? "#fee2e2" : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isListening ? "#fecaca" : "#f1f5f9",
                      },
                      "&:disabled": {
                        color: "#cbd5e1",
                        backgroundColor: "transparent",
                      },
                      animation: isListening
                        ? "voiceMicPulse 1.5s ease-in-out infinite"
                        : "none",
                      "@keyframes voiceMicPulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.65 },
                      },
                    }}
                  >
                    {isListening ? (
                      <MicOffIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <MicIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                aria-label={sending ? "Sending" : "Send"}
                onClick={send}
                disabled={!input.trim() || sending}
                size="small"
                sx={{
                  flexShrink: 0,
                  alignSelf: "center",
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  color: "#ffffff",
                  backgroundColor: BRAND_ACCENT,
                  padding: 0,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: BRAND_ACCENT_HOVER,
                    boxShadow: "0 2px 8px rgba(63, 73, 167, 0.3)",
                  },
                  "&:disabled": {
                    backgroundColor: "#cbd5e1",
                    color: "#94a3b8",
                    opacity: 0.7,
                  },
                }}
              >
                {sending ? (
                  <CircularProgress size={14} sx={{ color: "#ffffff" }} />
                ) : (
                  <SendRoundedIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}

      <Tooltip
        title={open ? "Close assistant" : "Help & workflows"}
        placement="left"
      >
        <IconButton
          aria-label={open ? "Close chat assistant" : "Open chat assistant"}
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: 24,
            height: 24,
            minWidth: 24,
            padding: 0,
            borderRadius: "50%",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            boxShadow: `0 1px 5px ${alpha(APP_HEADER_NAVY, 0.22)}`,
            transition: "background-color 0.2s ease, filter 0.2s ease",
            "@keyframes launcherSoftBlink": {
              "0%, 100%": { filter: "brightness(1)" },
              "50%": { filter: "brightness(1.04)" },
            },
            ...(!open && {
              animation: "launcherSoftBlink 7s ease-in-out infinite",
            }),
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
            "&:hover": {
              animationPlayState: "paused",
            },
            ...(isDark
              ? {
                  bgcolor: alpha("APP_HEADER_NAVY", 0.88),
                  color: alpha("#ffffff", 0.96),
                  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                  boxShadow: `0 1px 6px ${alpha("#000", 0.28)}`,
                  "&:hover": {
                    bgcolor: alpha(APP_HEADER_NAVY, 0.96),
                    borderColor: alpha(theme.palette.common.white, 0.2),
                    boxShadow: `0 2px 10px ${alpha("#000", 0.35)}`,
                  },
                }
              : {
                  bgcolor: alpha(APP_HEADER_NAVY, 0.86),
                  color: "#ffffff",
                  border: `1px solid ${alpha(APP_HEADER_NAVY, 0.22)}`,
                  "&:hover": {
                    bgcolor: alpha(APP_HEADER_NAVY, 0.94),
                    borderColor: alpha(APP_HEADER_NAVY, 0.35),
                    boxShadow: `0 2px 10px ${alpha(APP_HEADER_NAVY, 0.28)}`,
                  },
                }),
          }}
        >
          <FaceRetouchingNaturalIcon sx={{ fontSize: 13, color: "inherit" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default FloatingChatbot;
